<template>
  <div class="matching-suggestions-slider" ref="sliderRef">
    <div
      class="slider-arrow slider-arrow__left"
      ref="sliderLeftArrowRef"
      :style="`opacity: ${mentorsProfilesRef.length > 1 ? 1 : 0}`"
    >
      <ArrowIcon color="primary" :rotation="180" />
    </div>
    <div v-if="mentorsProfilesRef.length" class="matching-suggestions-slider__slides-wrapper">
      <div
        v-for="(mentorDataRef, key) in mentorsProfilesRef"
        :key="mentorDataRef.id"
        class="mentor-card-wrapper"
        data-test-id="mentor-card-wrapper"
      >
        <div data-test-id="slide-number" class="slide-number">{{ key + 1 }} / {{ mentorsProfilesRef.length }}</div>
        <div class="mentor-card">
          <div class="mentor-card-info" v-if="mentorDataRef">
            <Avatar :user="mentorDataRef" />
            <div class="mentor-card-info__info">
              <span data-test-id="matching-suggestion-mentor-name" class="user-name"
                >{{ mentorDataRef.firstName }} {{ mentorDataRef.lastName }}</span
              >
              <p class="user-sub-info">{{ t('common.mentor.capitalize') }}</p>
            </div>
          </div>
          <div class="mentor-info">
            <span v-if="mentorDataRef.description" v-html="mentorDataRef.description"></span>
            <span
              v-html="
                fillTemplate(
                  mentorMatchingTemplateRef ? mentorMatchingTemplateRef : '',
                  mentorDataRef.matchingCriteria ? mentorDataRef.matchingCriteria : [],
                ) || `Une erreur est survenue lors du chargement de la description`
              "
            >
            </span>
          </div>
          <div class="c-btns-container c-btn-center">
            <CustomButton
              usage="button"
              type="button"
              color="primary"
              text="Choisir ce mentor"
              data-test-id="matching-popup-choose-mentor"
              @emit:click="openConfirmModal(mentorDataRef)"
            />
          </div>
        </div>
      </div>
    </div>
    <LoadingContent v-else />
    <div
      class="slider-arrow slider-arrow__right"
      data-test-id="matching-popup-slider-right-arrow"
      ref="sliderRightArrowRef"
      :style="`opacity: ${mentorsProfilesRef.length > 1 ? 1 : 0}`"
    >
      <ArrowIcon color="primary" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import useMatchingInformation from '@/features/matching/hooks/use-matching-information.hook'
import type { MenteeMatchingResponseType } from '@/features/matching/types'
import { confirmMatchFn } from '@/features/matching/utils'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import ArrowIcon from '@/features/ui/components/icons/arrow-icon.vue'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import { selectGetMenteeMatchingModal, selectGetNewMatchingModal } from '@/features/ui/store/modal/modal.selectors'
import type { MenteeMatchingModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { V1GetUser } from '@/features/users/api'
import Avatar from '@/features/users/components/avatar/avatar.vue'
import { V1ToV2UserDataMiddleware } from '@/features/users/hooks/use-user.hook'
import type { V2UserDetailsDataType } from '@/features/users/types'
import useRouter from '@/hooks/use-router.hook'
import useToast from '@/hooks/use-toasts.hook'
import useWindowSize from '@/hooks/use-window-size.hook'
import { useI18n } from '@/lib/i18n'
import { ANALYTICS_EVENTS, trackEvent } from '@/lib/posthog'

const { mentorMatchingTemplateRef, fillTemplate } = useMatchingInformation({
  onSubmitCallback: () => null,
})

const props = defineProps<{
  mentors: MenteeMatchingResponseType['mentors']
}>()

const { widthRef } = useWindowSize()

const mentorsIdsRef = computed(() => props.mentors.map((mentor) => mentor.mentorId))
const mentorsProfilesRef = ref<Partial<V2UserDetailsDataType>[]>([])

const sliderLeftArrowRef = ref<HTMLElement | null>(null)
const sliderRightArrowRef = ref<HTMLElement | null>(null)
const sliderRef = ref<HTMLElement | null>(null)

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)

const newMatchingModalRef = selectGetNewMatchingModal(modalsStore)
const menteeMatchingModalRef = selectGetMenteeMatchingModal(modalsStore)

// Analytics: track results view once and time to select
const hasTrackedResultsView = ref(false)
const sliderMountTime = ref(Date.now())

watch(mentorsProfilesRef, (profiles) => {
  if (profiles.length > 0 && !hasTrackedResultsView.value) {
    hasTrackedResultsView.value = true
    sliderMountTime.value = Date.now()
    trackEvent(ANALYTICS_EVENTS.MATCHING_RESULTS_VIEW, {
      program_id: selectedProgramIdRef.value,
      role: 'mentee',
      results_count: profiles.length,
      attempt_number: menteeMatchingModalRef.value?.attachedData?.attemptNumber ?? 1,
      has_ai_suggestions: true,
    })
    // Store sliderMountTime in modal attachedData for abandon tracking
    modalsStore.updateModalAttachedData<MenteeMatchingModalType>('mentee-matching', {
      ...menteeMatchingModalRef.value.attachedData,
      sliderMountTime: sliderMountTime.value,
    })
  }
})

// Analytics: track LinkedIn clicks via event delegation
const handleLinkedinClick = (e: Event) => {
  const target = e.target as HTMLElement
  const link = target.closest('a[href]') as HTMLAnchorElement | null
  if (link && link.href.toLowerCase().includes('linkedin')) {
    const mentorCard = link.closest('.mentor-card-wrapper')
    const allCards = sliderRef.value?.querySelectorAll('.mentor-card-wrapper')
    const position = allCards ? Array.from(allCards).indexOf(mentorCard as Element) : 0

    trackEvent(ANALYTICS_EVENTS.MATCHING_LINKEDIN_CLICK, {
      program_id: selectedProgramIdRef.value,
      role: 'mentee',
      position_in_list: position + 1,
    })
  }
}

onMounted(() => {
  sliderRef.value?.addEventListener('click', handleLinkedinClick)
})

onUnmounted(() => {
  sliderRef.value?.removeEventListener('click', handleLinkedinClick)
})

// Détecter quel modal est ouvert pour obtenir le bon groupId et nom de modal
const activeModalNameRef = computed<'new-matching' | 'mentee-matching'>(() => {
  return menteeMatchingModalRef.value.isOpen ? 'mentee-matching' : 'new-matching'
})

const matchedGroupIdRef = computed(() => {
  if (activeModalNameRef.value === 'mentee-matching') {
    return (menteeMatchingModalRef.value.attachedData as any).groupId
  }
  return (newMatchingModalRef.value.attachedData as any).groupId
})

watch(
  mentorsIdsRef,
  async (newMentorsIds) => {
    if (newMentorsIds.length) {
      mentorsProfilesRef.value = await Promise.all(
        newMentorsIds.map(async (mentorId) => {
          const mentorProfile = await V1GetUser(mentorId, selectedProgramIdRef.value!)
          return V1ToV2UserDataMiddleware(mentorProfile)!
        }),
      )
    }
  },
  { immediate: true },
)

// slider logic
watch(
  [mentorsProfilesRef, widthRef],
  () => {
    let currentSlide = 0
    const leftArrow = sliderLeftArrowRef.value!
    const rightArrow = sliderRightArrowRef.value!

    const slider = sliderRef.value
    if (!slider) return

    const slides = slider.querySelectorAll<HTMLDivElement>('.mentor-card-wrapper')!
    const slideWidth = slides[0].clientWidth

    Array.from(slides).forEach((slide) => {
      slide.style.transform = ``
    })

    if (mentorsProfilesRef.value.length) {
      const updateSlides = () => {
        Array.from(slides).forEach((slide) => {
          slide.style.transform = `translateX(${-currentSlide * slideWidth}px)`
        })
      }

      const nextSlide = () => {
        if (currentSlide >= slides.length - 1) {
          currentSlide = 0
        } else {
          currentSlide++
        }
        updateSlides()
      }

      const prevSlide = () => {
        if (currentSlide <= 0) {
          currentSlide = slides.length - 1
        } else {
          currentSlide--
        }
        updateSlides()
      }

      leftArrow.addEventListener('click', prevSlide)
      rightArrow.addEventListener('click', nextSlide)
    }
  },
  { immediate: true, flush: 'post' },
)

const toast = useToast()
const { accountRef } = useAccount()
const router = useRouter()
const { t } = useI18n()

const isSubmittingRef = ref(false)

function openConfirmModal(mentorDataRef: Partial<V2UserDetailsDataType>) {
  trackEvent(ANALYTICS_EVENTS.MATCHING_MENTOR_SELECT, {
    program_id: selectedProgramIdRef.value,
    role: 'mentee',
    profiles_viewed_total: mentorsProfilesRef.value.length,
    results_count: props.mentors.length,
    attempt_number: menteeMatchingModalRef.value?.attachedData?.attemptNumber ?? 1,
    time_to_select_ms: Date.now() - sliderMountTime.value,
  })

  modalsStore.updateModalAttachedData('confirm-action', {
    title: t('matching.confirmMentorModal.title'),
    type: 'danger',
    information: t('matching.confirmMentorModal.text', {
      firstName: mentorDataRef.firstName ?? '',
      lastName: mentorDataRef.lastName ?? '',
    }),
    btnText: t('common.continue'),
    fn: () => {
      confirmMatch(mentorDataRef.id)
      modalsStore.toggleModal('confirm-action', false)
    },
    cancelFn: () => {
      modalsStore.toggleModal('confirm-action', false)
    },
  })

  modalsStore.setModalStep('confirm-action', 'confirm-action')
  modalsStore.toggleModal('confirm-action', true)
}

async function confirmMatch(selectedMentorId: string | undefined) {
  if (!selectedMentorId) {
    toast?.error(t('error.common'))
    return
  }
  await confirmMatchFn({
    accountRef,
    isSubmittingRef,
    matchedGroupIdRef,
    selectedMentorId,
    selectedProgramIdRef,
    t,
    router,
    toast,
    modalName: activeModalNameRef.value,
  })
}
</script>

<style lang="scss">
@import './matching-suggestions-slider.scss';
</style>
